import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
    Calendar,
    Upload,
    IndianRupee,
    ArrowLeft,
    MoreVertical,
    ChevronDown,
    Check,
    ChevronLeft,
    X,
    Loader,
    Link,
    Search,
} from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import { resolveTransactionFeedback } from "utils/transactionFeedback";
import { getLinkedTasksBySite } from "utils/linkedTasks";
import {
    buildWorkerPaymentTaskUpdate,
    createLinkedTaskUpdate,
} from "utils/taskAutoUpdates";

const WorkerSelectionModal = ({ onClose, onSelectWorker }) => {
    const [workers, setWorkers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchWorkers = async () => {
            const site = localStorage.getItem("site");
            const accessToken = localStorage.getItem("access");
            try {
                const response = await fetch(`${BASE_URL}/worker/workers-page/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        worker_type: "Worker",
                        sites: parseInt(site),
                    }),
                });
                const data = await response.json();
                if (data.status) {
                    setWorkers(data.data);
                }
            } catch (error) {
                console.error("Error fetching workers:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkers();
    }, []);

    return (
            <div className="bg-white rounded-xl border border-gray-200 p-4 w-full shadow-sm mt-2">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Select Worker</h2>
                    <button onClick={onClose}>
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-6 text-sm text-gray-600">
                        Loading...
                    </div>
                ) : (
                    <div className="max-h-96 overflow-y-auto">
                        {workers.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg border border-gray-200 my-4">
                                <p className="text-gray-600 mb-1">No workers found</p>
                                <p className="text-gray-500">
                                    Add new workers from the workers tab
                                </p>
                            </div>
                        ) : (
                            workers.map((worker) => (
                                <button
                                    key={worker.id}
                                    className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100"
                                    onClick={() => onSelectWorker(worker)}
                                >
                                    <div className="font-medium">{worker.worker_name}</div>
                                    <div className="text-sm text-gray-500">
                                        {worker.worker_workname} - {worker.worker_workcategory}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
    );
};

const WorkerPayment = ({ onClose }) => {
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [selectedWorkerAttendance, setSelectedWorkerAttendance] =
        useState(null);
    const [selectedWorkerPayments, setSelectedWorkerPayments] = useState([]);
    const [selectedWorkerWageHistory, setSelectedWorkerWageHistory] = useState(
        [],
    );
    const [remarks, setRemarks] = useState("");
    const [amount, setAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [paymentDate, setPaymentDate] = useState("");
    const [paymentType, setPaymentType] = useState("Wage");
    const [dateMode, setDateMode] = useState("Now");
    const [dateValue, setDateValue] = useState(() => {
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${yyyy}-${mm}-${dd}`;
    });

    const [showWorkerModal, setShowWorkerModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaveClicked, setIsSaveClicked] = useState(false);
    const [receiptImage, setReceiptImage] = useState(null);
    const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
    const [workerTransactionCatagory, setWorkerTransactionCatagory] =
        useState(null);
    const receiptInputRef = useRef(null);
    const paymentId = "WP_ID_01";

    // Multi-worker payment states
    const [isMultiPayment, setIsMultiPayment] = useState(false);
    const formatAmountPreview = (value) => {
        const numericValue = Number(value);
        if (value === "" || Number.isNaN(numericValue)) {
            return "Rs. 0";
        }
        return `Rs. ${numericValue.toLocaleString("en-IN")}`;
    };
    const [allWorkers, setAllWorkers] = useState([]);
    const [defaulterWorkers, setDefaulterWorkers] = useState([]);
    const [loadingDefaulters, setLoadingDefaulters] = useState(false);
    const { addAlert } = useAlert();

    // BoQ linking states
    const [boqItems, setBoqItems] = useState([]);
    const [showBoQModal, setShowBoQModal] = useState(false);
    const [selectedBoQ, setSelectedBoQ] = useState(null);
    const [selectedBoQsByWorker, setSelectedBoQsByWorker] = useState({});
    const [boqPickerWorkerId, setBoqPickerWorkerId] = useState(null);
    const [boqSearchQuery, setBoqSearchQuery] = useState("");

    // Fetch linked tasks
    useEffect(() => {
        const fetchLinkedTasks = async () => {
            try {
                const linkedTasks = await getLinkedTasksBySite();
                setBoqItems(linkedTasks);
            } catch (error) {
                console.error("Error fetching linked tasks:", error);
            }
        };
        fetchLinkedTasks();
    }, []);

    useEffect(() => {
        const fetchTransactionCatagories = async () => {
            try {
                const accessToken = localStorage.getItem("access");
                const response = await fetch(
                    `${BASE_URL}/finance/get-all-finance-catagory/`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${accessToken}`,
                        },
                    },
                );
                if (response.ok) {
                    const res = await response.json();
                    const data = res.data;
                    const purchaseCatagory = data.find(
                        (cat) => cat.catagory_name === "Worker Transaction",
                    );
                    if (purchaseCatagory) {
                        setWorkerTransactionCatagory(purchaseCatagory.id);
                    }
                } else {
                    console.error("Failed to fetch material transaction catagories");
                }
            } catch (error) {
                console.error("Error fetching material transaction catagories:", error);
            }
        };
        fetchTransactionCatagories();
    }, []);

    const getSelectedWorkerAttendance = () => {
        // Fetch and set selected worker attendance data here
        const accessToken = localStorage.getItem("access");
        try {
            fetch(
                `${BASE_URL}/worker/get-a-worker-attendance/${selectedWorker?.worker_id}/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (data.status) {
                        setSelectedWorkerAttendance(data.data);
                    }
                });
        } catch (error) {
            console.error("Error fetching worker attendance:", error);
        }
    };

    const getSelectedWorkerPayments = () => {
        // Fetch and set selected worker payments data here
        const accessToken = localStorage.getItem("access");
        try {
            fetch(`${BASE_URL}/finance/get-worker-transactions-of-a-worker/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    worker_id: selectedWorker.worker_id,
                    sites: parseInt(localStorage.getItem("site")),
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.status) {
                        setSelectedWorkerPayments(data.data);
                    }
                });
        } catch (error) {
            console.error("Error fetching worker payments:", error);
        }
    };

    const getSelectedWorkerWageHistory = () => {
        const accessToken = localStorage.getItem("access");
        try {
            fetch(`${BASE_URL}/worker/get-workerpayment-of-worker/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ worker_id: selectedWorker.worker_id }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.status && Array.isArray(data.data)) {
                        setSelectedWorkerWageHistory(data.data);
                    } else {
                        setSelectedWorkerWageHistory([]);
                    }
                });
        } catch (error) {
            console.error("Error fetching worker wage history:", error);
            setSelectedWorkerWageHistory([]);
        }
    };

    useEffect(() => {
        if (selectedWorker) {
            getSelectedWorkerAttendance();
            getSelectedWorkerPayments();
            getSelectedWorkerWageHistory();
        }
    }, [selectedWorker]);

    // Fetch all workers and calculate defaulters for multi-payment
    useEffect(() => {
        if (isMultiPayment) {
            fetchAllWorkersWithBalances();
        }
    }, [isMultiPayment]);

    const fetchAllWorkersWithBalances = async () => {
        setLoadingDefaulters(true);
        const site = localStorage.getItem("site");
        const accessToken = localStorage.getItem("access");

        try {
            // Fetch all workers
            const workersResponse = await fetch(`${BASE_URL}/worker/workers-page/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    worker_type: "Worker",
                    sites: parseInt(site),
                }),
            });
            const workersData = await workersResponse.json();

            if (!workersData.status) {
                setLoadingDefaulters(false);
                return;
            }

            const workers = workersData.data;
            const workersWithBalances = [];

            // For each worker, fetch attendance and payments
            for (const worker of workers) {
                try {
                    // Fetch attendance
                    const attendanceResponse = await fetch(
                        `${BASE_URL}/worker/get-a-worker-attendance/${worker.worker_id}/`,
                        {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${accessToken}`,
                            },
                        },
                    );
                    const attendanceData = await attendanceResponse.json();

                    // Fetch payments
                    const paymentsResponse = await fetch(
                        `${BASE_URL}/finance/get-worker-transactions-of-a-worker/`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${accessToken}`,
                            },
                            body: JSON.stringify({
                                worker_id: worker.worker_id,
                                sites: parseInt(site),
                            }),
                        },
                    );
                    const paymentsData = await paymentsResponse.json();

                    // Fetch wage history
                    const wageHistoryResponse = await fetch(
                        `${BASE_URL}/worker/get-workerpayment-of-worker/`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${accessToken}`,
                            },
                            body: JSON.stringify({
                                worker_id: worker.worker_id,
                            }),
                        },
                    );
                    const wageHistoryData = await wageHistoryResponse.json();
                    const wageHistory =
                        wageHistoryData.status && Array.isArray(wageHistoryData.data)
                            ? wageHistoryData.data
                            : [];
                    // Sort descending
                    const sortedHistory = [...wageHistory].sort(
                        (a, b) => new Date(b.start_date) - new Date(a.start_date),
                    );
                    const currentRate = worker.daily_wage || 0;

                    const getRate = (dateStr) => {
                        if (!sortedHistory.length) return currentRate;
                        const target = new Date(dateStr);
                        const found = sortedHistory.find(
                            (w) => new Date(w.start_date) <= target,
                        );
                        return found
                            ? parseFloat(found.amount)
                            : parseFloat(sortedHistory[sortedHistory.length - 1].amount) || 0;
                    };

                    // Calculate earnings
                    let baseEarnings = 0;
                    let overtimeEarnings = 0;

                    if (attendanceData.status && attendanceData.data) {
                        attendanceData.data.forEach((attendance) => {
                            if (attendance.attendance === "Present") {
                                const dailyWage = getRate(attendance.attendance_date);
                                if (attendance.full_day) {
                                    baseEarnings += dailyWage;
                                } else if (attendance.half_day) {
                                    baseEarnings += dailyWage / 2;
                                }
                                if (attendance.overtime_wage) {
                                    overtimeEarnings += attendance.overtime_wage;
                                }
                            }
                        });
                    }

                    const totalEarnings = Math.round(baseEarnings + overtimeEarnings);

                    // Calculate total payments
                    const totalPayments = paymentsData.status
                        ? paymentsData.data.reduce(
                            (sum, payment) => sum + (payment.amount || 0),
                            0,
                        )
                        : 0;

                    const balance = totalEarnings - totalPayments;

                    workersWithBalances.push({
                        ...worker,
                        totalEarnings,
                        totalPayments,
                        balance,
                    });
                } catch (error) {
                    console.error(`Error processing worker ${worker.worker_id}:`, error);
                }
            }

            // Filter only defaulters (balance > 0)
            const defaulters = workersWithBalances.filter(
                (worker) => worker.balance > 0,
            );

            setAllWorkers(workersWithBalances);
            setDefaulterWorkers(defaulters);

            // Set the amount to total pending
            const totalPending = defaulters.reduce((sum, w) => sum + w.balance, 0);
            setAmount(totalPending.toString());
        } catch (error) {
            console.error("Error fetching workers with balances:", error);
        } finally {
            setLoadingDefaulters(false);
        }
    };

    // useMemo to calculate earnings based on attendance
    const calculatedEarnings = useMemo(() => {
        if (!selectedWorkerAttendance || !selectedWorker) {
            return { totalEarnings: 0, baseEarnings: 0, overtimeEarnings: 0 };
        }

        // sort history once
        const history = selectedWorkerWageHistory || [];
        const sortedHistory = [...history].sort(
            (a, b) => new Date(b.start_date) - new Date(a.start_date),
        );
        const currentRate = selectedWorker.daily_wage || 0;

        const getRate = (dateStr) => {
            if (!history.length) return currentRate;
            const target = new Date(dateStr);
            const found = sortedHistory.find((w) => new Date(w.start_date) <= target);
            return found
                ? parseFloat(found.amount)
                : parseFloat(sortedHistory[sortedHistory.length - 1].amount) || 0;
        };

        let baseEarnings = 0;
        let overtimeEarnings = 0;
        let deductions = 0;
        // Calculate earnings from attendance records
        selectedWorkerAttendance.forEach((attendance) => {
            if (attendance.attendance === "Present") {
                // Calculate base earnings based on full_day or half_day
                // const dailyWage = selectedWorker.daily_wage || 0;
                const dailyWage = getRate(attendance.attendance_date);

                baseEarnings += dailyWage;
                if (attendance.half_day) {
                    deductions += dailyWage / 2;
                }

                // Add overtime earnings
                if (attendance.overtime_wage) {
                    overtimeEarnings += attendance.overtime_wage;
                }
            }
        });

        const totalEarnings = baseEarnings + overtimeEarnings - deductions;

        return {
            totalEarnings: Math.round(totalEarnings),
            baseEarnings: Math.round(baseEarnings),
            overtimeEarnings: Math.round(overtimeEarnings),
            deductions: Math.round(deductions),
        };
    }, [selectedWorkerAttendance, selectedWorker, selectedWorkerWageHistory]);

    const totalPayment = useMemo(() => {
        return selectedWorkerPayments.reduce(
            (sum, payment) => sum + (payment.amount || 0),
            0,
        );
    }, [selectedWorkerPayments]);

    const paymentRemaining = useMemo(() => {
        return calculatedEarnings.totalEarnings - totalPayment;
    }, [calculatedEarnings, totalPayment]);

    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            setReceiptImage(file);
            setReceiptPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleClearReceipt = () => {
        setReceiptImage(null);
        if (receiptPreviewUrl) {
            URL.revokeObjectURL(receiptPreviewUrl);
        }
        setReceiptPreviewUrl(null);
        if (receiptInputRef.current) {
            receiptInputRef.current.value = "";
        }
    };

    useEffect(() => {
        return () => {
            if (receiptPreviewUrl) {
                URL.revokeObjectURL(receiptPreviewUrl);
            }
        };
    }, [receiptPreviewUrl]);

    const getWorkerPaymentKey = (worker) => worker?.worker_id || worker?.id;

    const openSingleBoQModal = () => {
        setBoqPickerWorkerId(null);
        setBoqSearchQuery("");
        setShowBoQModal(true);
    };

    const openMultiWorkerBoQModal = (worker) => {
        setBoqPickerWorkerId(getWorkerPaymentKey(worker));
        setBoqSearchQuery("");
        setShowBoQModal(true);
    };

    const closeBoQModal = () => {
        setShowBoQModal(false);
        setBoqPickerWorkerId(null);
        setBoqSearchQuery("");
    };

    const removeMultiWorkerBoQ = (worker) => {
        const workerKey = getWorkerPaymentKey(worker);
        setSelectedBoQsByWorker((current) => {
            const next = { ...current };
            delete next[workerKey];
            return next;
        });
    };

    const handleSave = async () => {
        if (isMultiPayment) {
            // Handle multi-worker payment
            await handleMultiWorkerPayment();
        } else {
            // Handle single worker payment
            await handleSingleWorkerPayment();
        }
    };

    const handleSingleWorkerPayment = async () => {
        if (!selectedWorker || !amount || isLoading || isSaveClicked) return;

        setIsLoading(true);
        setIsSaveClicked(true);

        const site = localStorage.getItem("site");

        let formattedDate = dateValue;
        if (dateMode === "Now") {
            const d = new Date();
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const yyyy = d.getFullYear();
            formattedDate = `${yyyy}-${mm}-${dd}`;
        }

        const formData = new FormData();
        formData.append("payment_type", paymentType);
        formData.append("amount", parseInt(amount));
        formData.append("worker_id", selectedWorker.worker_id || selectedWorker.id);
        formData.append("transaction_date", formattedDate);
        formData.append("transaction_method", paymentMethod);
        formData.append("comments", remarks);
        formData.append("transaction_catagory_id", workerTransactionCatagory);
        formData.append("site", parseInt(site));

        if (selectedBoQ?.task) {
            formData.append("task_id", selectedBoQ.task);
        }

        if (receiptImage) {
            formData.append("receipt_image", receiptImage);
        }

        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
                `${BASE_URL}/finance/create-worker-transaction/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: formData,
                },
            );
            const data = await response.json();
            const feedback = resolveTransactionFeedback(data, {
                successMessage: "Payment created successfully",
                errorMessage: "Failed to create payment",
            });

            if (feedback.success) {
                if (feedback.approvalRequired) {
                    addAlert(feedback.message, feedback.alertType);
                }
                await createLinkedTaskUpdate(
                    selectedBoQ?.task,
                    buildWorkerPaymentTaskUpdate({
                        record: data,
                        amount,
                        workerName: selectedWorker.worker_name,
                        paymentType,
                    }),
                );
                onClose();
            } else {
                addAlert(feedback.message, feedback.alertType);
                setIsSaveClicked(false);
            }
        } catch (error) {
            console.error("Error creating payment:", error);
            addAlert(error.message || "Failed to create payment", "error");
            setIsSaveClicked(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMultiWorkerPayment = async () => {
        if (defaulterWorkers.length === 0 || !amount || isLoading || isSaveClicked)
            return;

        const totalPending = defaulterWorkers.reduce(
            (sum, w) => sum + w.balance,
            0,
        );

        // Validate exact amount
        if (parseInt(amount) !== totalPending) {
            alert(
                `You must pay exactly ₹${totalPending.toLocaleString(
                    "en-IN",
                )} for all defaulters.`,
            );
            return;
        }

        setIsLoading(true);
        setIsSaveClicked(true);

        const site = localStorage.getItem("site");
        const accessToken = localStorage.getItem("access");

        let formattedDate = dateValue;
        if (dateMode === "Now") {
            const d = new Date();
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const yyyy = d.getFullYear();
            formattedDate = `${yyyy}-${mm}-${dd}`;
        }

        try {
            // Create payment for each defaulter
            const paymentPromises = defaulterWorkers.map(async (worker) => {
                const formData = new FormData();
                formData.append("payment_type", paymentType);
                formData.append("amount", worker.balance);
                formData.append("worker_id", worker.worker_id || worker.id);
                formData.append("transaction_date", formattedDate);
                formData.append("transaction_method", paymentMethod);
                formData.append(
                    "comments",
                    remarks || `Multi-worker payment - ${worker.worker_name}`,
                );
                formData.append("transaction_catagory_id", workerTransactionCatagory);
                formData.append("site", parseInt(site));

                const selectedWorkerBoQ =
                    selectedBoQsByWorker[getWorkerPaymentKey(worker)];
                if (selectedWorkerBoQ?.task) {
                    formData.append("task_id", selectedWorkerBoQ.task);
                }

                if (receiptImage) {
                    formData.append("receipt_image", receiptImage);
                }

                const response = await fetch(`${BASE_URL}/finance/create-worker-transaction/`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: formData,
                });

                return {
                    response,
                    selectedWorkerBoQ,
                    worker,
                };
            });

            const responses = await Promise.all(
                paymentPromises.map(async (paymentPromise) => {
                    const { response, selectedWorkerBoQ, worker } =
                        await paymentPromise;
                    let data = null;

                    try {
                        data = await response.json();
                    } catch (_) {
                        data = null;
                    }

                    return {
                        data,
                        response,
                        selectedWorkerBoQ,
                        worker,
                        feedback: resolveTransactionFeedback(data, {
                            successMessage: "Payment created successfully",
                            errorMessage: "Failed to create payment",
                        }),
                    };
                }),
            );

            const failedResponse = responses.find(
                ({ response, feedback }) => !response.ok || !feedback.success,
            );

            if (!failedResponse) {
                const approvalResponse = responses.find(
                    ({ feedback }) => feedback.approvalRequired,
                );
                if (approvalResponse) {
                    addAlert(
                        approvalResponse.feedback.message,
                        approvalResponse.feedback.alertType,
                    );
                }
                await Promise.all(
                    responses.map(({ data, selectedWorkerBoQ, worker }) =>
                        createLinkedTaskUpdate(
                            selectedWorkerBoQ?.task,
                            buildWorkerPaymentTaskUpdate({
                                record: data,
                                amount: worker.balance,
                                workerName: worker.worker_name,
                                paymentType,
                            }),
                        ),
                    ),
                );
                onClose();
            } else {
                addAlert(
                    failedResponse.feedback.message ||
                    "Some payments failed. Please check and try again.",
                    "error",
                );
                setIsSaveClicked(false);
            }
        } catch (error) {
            console.error("Error creating multi-worker payments:", error);
            addAlert(error.message || "Error processing payments. Please try again.", "error");
            setIsSaveClicked(false);
        } finally {
            setIsLoading(false);
        }
    };

    const getNormalizedTaskStatus = (item) =>
        String(
            item?.task_status ||
            item?.taskStatus ||
            item?.status ||
            item?.task_details?.task_status ||
            item?.task_details?.status ||
            "",
        ).toLowerCase();
    const isCompletedTask = (item) => getNormalizedTaskStatus(item).includes("complete");
    const filteredBoqItems = boqItems.filter(
        (item) =>
            !isCompletedTask(item) &&
            ((item.task_name || item.item_name || "").toLowerCase().includes(boqSearchQuery.toLowerCase()) ||
                item.sectionName.toLowerCase().includes(boqSearchQuery.toLowerCase())),
    );
    const getTaskStatusBucket = (item) => {
        const normalizedStatus = getNormalizedTaskStatus(item);

        if (
            normalizedStatus.includes("halt") ||
            normalizedStatus.includes("hold") ||
            normalizedStatus.includes("pause")
        ) {
            return "halted";
        }
        if (
            normalizedStatus.includes("not started") ||
            normalizedStatus.includes("not_started")
        ) {
            return "notStarted";
        }
        if (
            normalizedStatus.includes("ongoing") ||
            normalizedStatus.includes("in progress") ||
            normalizedStatus.includes("in_progress")
        ) {
            return "ongoing";
        }
        return "notStarted";
    };
    const groupedBoqItems = [
        { key: "ongoing", title: "Ongoing" },
        { key: "notStarted", title: "Not Started" },
        { key: "halted", title: "Halted" },
    ]
        .map((group) => ({
            ...group,
            items: filteredBoqItems.filter((item) => getTaskStatusBucket(item) === group.key),
        }))
        .filter((group) => group.items.length > 0);

    const activeBoQSelection =
        boqPickerWorkerId === null
            ? selectedBoQ
            : selectedBoQsByWorker[boqPickerWorkerId] || null;

    const handleSelectBoQ = (item) => {
        if (boqPickerWorkerId === null) {
            setSelectedBoQ(item);
        } else {
            setSelectedBoQsByWorker((current) => ({
                ...current,
                [boqPickerWorkerId]: item,
            }));
        }
        closeBoQModal();
    };

    const renderBoQSelectionModal = () => (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-50"
            onClick={closeBoQModal}
        >
            <div
                className="bg-white rounded-xl w-full max-w-md max-h-[70vh] flex flex-col shadow-2xl overflow-hidden border border-gray-100"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                    <h3 className="text-xl font-medium text-[#232323]">Link to BoQ Task</h3>
                    <button
                        type="button"
                        onClick={closeBoQModal}
                        className="p-1 rounded-full hover:bg-gray-200 text-gray-500 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4 border-b bg-white">
                    <div className="relative">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={16}
                        />
                        <input
                            type="text"
                            placeholder="Search BoQ items or sections..."
                            value={boqSearchQuery}
                            onChange={(e) => setBoqSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm outline-none transition-all"
                            autoFocus
                        />
                    </div>
                </div>

                <div className="overflow-y-auto flex-1 p-2 bg-white">
                    {groupedBoqItems.map((group) => (
                        <div key={group.key} className="mb-2">
                            <p className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                {group.title}
                            </p>
                            {group.items.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => handleSelectBoQ(item)}
                                    className={`p-3 rounded-lg cursor-pointer mb-1 transition-all border ${
                                        activeBoQSelection?.id === item.id
                                            ? "bg-purple-50 border-purple-200"
                                            : "hover:bg-gray-50 border-transparent"
                                    }`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="text-sm font-semibold text-gray-900">
                                                {item.task_name || item.item_name}
                                            </div>
                                            <div className="text-[11px] text-gray-500 mt-0.5">
                                                {item.sectionName}
                                            </div>
                                        </div>
                                        {activeBoQSelection?.id === item.id && (
                                            <div className="bg-purple-600 h-2 w-2 rounded-full"></div>
                                        )}
                                    </div>
                                    <div className="mt-2 flex items-center">
                                        <span className="text-[10px] font-medium bg-gray-100 px-2 py-0.5 rounded-md text-gray-600">
                                            {item.quantity} {item.uom}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                    {filteredBoqItems.length === 0 && (
                        <div className="py-12 text-center">
                            <p className="text-gray-400 text-sm">No matching tasks found</p>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t bg-gray-50 flex gap-3">
                    <button
                        type="button"
                        onClick={closeBoQModal}
                        className="flex-1 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex h-full flex-col bg-white">
            <div className="flex flex-row items-center justify-between px-4 h-16 py-2 border-b border-gray-100 shrink-0">
                <h2 className="text-xl font-medium text-[#232323]">
                    Worker Payment
                </h2>

                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-[#232323]">
                        Multi-Worker Payment
                    </span>
                    <button
                        onClick={() => {
                            setIsMultiPayment(!isMultiPayment);
                            setSelectedWorker(null);
                            setSelectedBoQ(null);
                            setSelectedBoQsByWorker({});
                            setAmount("");
                        }}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                            isMultiPayment ? "bg-[#3B0069]" : "bg-gray-300"
                        }`}
                    >
                        <div
                            className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                isMultiPayment ? "transform translate-x-6" : ""
                            }`}
                        />
                    </button>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-2 py-3">
                <div className="space-y-6">
                    <div className="flex flex-col gap-6 rounded-xl border border-gray-100 bg-white p-4">
                {/* Left */}
                <div className="space-y-4">
                    {/* Worker Profile */}
                    {/* <div className="flex items-center gap-3">
            <div className="text-[#232323] text-[14px]">
              Payment ID.
              <span className="text-[12px] bg-gray-200 px-2 py-1 rounded-full ml-2 text-gray-600">
                {paymentId}
              </span>
            </div>
          </div> */}

                    {/* Conditional Rendering based on payment mode */}
                    {!isMultiPayment ? (
                        <>
                            {/* Single Worker Selection */}
                            <div>
                                <label className="block text-sm font-medium text-[#232323] mb-2">
                                    Select Worker
                                </label>
                                <button
                                    onClick={() => setShowWorkerModal(true)}
                                    className="w-full px-4 py-2 rounded-md bg-white border outline-none text-[#232323] text-left flex items-center justify-between"
                                >
                                    <span>
                                        {selectedWorker
                                            ? selectedWorker.worker_name
                                            : "Worker unassigned"}
                                    </span>
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                                {showWorkerModal && (
                                    <WorkerSelectionModal
                                        onClose={() => setShowWorkerModal(false)}
                                        onSelectWorker={(worker) => {
                                            setSelectedWorker(worker);
                                            setShowWorkerModal(false);
                                        }}
                                    />
                                )}
                            </div>
                            {!isMultiPayment && boqItems.length > 0 && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-[#232323] mb-2">
                                        Link to Task (Optional)
                                    </label>
                                    <div
                                        onClick={openSingleBoQModal}
                                        className="flex items-center justify-between p-3 bg-[#F9F5FF] rounded-lg cursor-pointer hover:bg-[#F4EBFF] transition-colors border border-dashed border-[#D6BBFB]"
                                    >
                                        {selectedBoQ ? (
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-[#6941C6]">{selectedBoQ.task_name || selectedBoQ.item_name}</span>
                                                <span className="text-[11px] text-[#7F56D9]">{selectedBoQ.sectionName}</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center text-gray-500 text-sm">
                                                <Link size={14} className="mr-2" />
                                                Select a task to link
                                            </div>
                                        )}
                                        <button type="button" className="text-[#7F56D9] text-xs font-semibold">
                                            {selectedBoQ ? "Change" : "Link"}
                                        </button>
                                    </div>
                                    {selectedBoQ && (
                                        <button
                                            type="button"
                                            onClick={() => setSelectedBoQ(null)}
                                            className="mt-1 text-[11px] text-red-500 hover:text-red-700 font-medium"
                                        >
                                            Remove link
                                        </button>
                                    )}
                                </div>
                            )}
                            {selectedWorker && (
                                <div className="rounded-lg p-4 border border-purple-200">
                                    <p className="text-[#232323] text-[16px] font-semibold mb-3">
                                        Payment Summary
                                    </p>

                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[#232323] text-[14px]">
                                                Total Earnings
                                            </span>
                                            <span className="text-[#3B0069] font-semibold text-[15px]">
                                                ₹
                                                {calculatedEarnings.totalEarnings.toLocaleString(
                                                    "en-IN",
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex justify-between items-center pl-4 text-[13px]">
                                            <span className="text-gray-600">Base Earnings</span>
                                            <span className="text-gray-700">
                                                ₹
                                                {calculatedEarnings.baseEarnings.toLocaleString(
                                                    "en-IN",
                                                )}
                                            </span>
                                        </div>

                                        {calculatedEarnings.overtimeEarnings > 0 && (
                                            <div className="flex justify-between items-center pl-4 text-[13px]">
                                                <span className="text-gray-600">Overtime Earnings</span>
                                                <span className="text-gray-700">
                                                    ₹
                                                    {calculatedEarnings.overtimeEarnings.toLocaleString(
                                                        "en-IN",
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {calculatedEarnings.deductions > 0 && (
                                            <div className="flex justify-between items-center pl-4 text-[13px]">
                                                <span className="text-gray-600">Deductions</span>
                                                <span className="text-gray-700">
                                                    - ₹
                                                    {calculatedEarnings.deductions.toLocaleString(
                                                        "en-IN",
                                                    )}
                                                </span>
                                            </div>
                                        )}

                                        <hr className="my-2" />

                                        <div className="flex justify-between items-center">
                                            <span className="text-[#232323] text-[14px]">
                                                Payment Made
                                            </span>
                                            <span className="text-red-600 font-semibold text-[15px]">
                                                - ₹{totalPayment.toLocaleString("en-IN")}
                                            </span>
                                        </div>

                                        <hr className="my-2 border-purple-300" />

                                        <div className="flex justify-between items-center bg-white rounded-md p-2">
                                            <span className="text-[#232323] text-[15px] font-semibold">
                                                Payment Remaining
                                            </span>
                                            <span
                                                className={`font-bold text-[16px] ${paymentRemaining >= 0
                                                    ? "text-green-600"
                                                    : "text-red-600"
                                                    }`}
                                            >
                                                ₹{Math.abs(paymentRemaining).toLocaleString("en-IN")}
                                                {paymentRemaining < 0 && " (Overpaid)"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Multi-Worker Payment Summary */}
                            <div className="rounded-lg p-4 border border-purple-200">
                                <p className="text-[#232323] text-[16px] font-semibold mb-3">
                                    Multi-Worker Payment Summary
                                </p>

                                {loadingDefaulters ? (
                                    <div className="text-center py-4">
                                        <Loader className="w-6 h-6 animate-spin mx-auto text-[#3B0069]" />
                                        <p className="text-sm text-gray-600 mt-2">
                                            Loading workers...
                                        </p>
                                    </div>
                                ) : defaulterWorkers.length === 0 ? (
                                    <div className="text-center py-4">
                                        <p className="text-gray-600">
                                            No workers with pending payments
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center bg-purple-50 p-2 rounded">
                                            <span className="text-[#232323] text-[14px] font-semibold">
                                                Total Defaulters
                                            </span>
                                            <span className="text-[#3B0069] font-bold text-[15px]">
                                                {defaulterWorkers.length}
                                            </span>
                                        </div>

                                        <div className="max-h-64 overflow-y-auto space-y-2 border rounded p-2">
                                            {defaulterWorkers.map((worker) => {
                                                const workerKey = getWorkerPaymentKey(worker);
                                                const selectedWorkerBoQ =
                                                    selectedBoQsByWorker[workerKey];

                                                return (
                                                    <div
                                                        key={worker.id}
                                                        className="text-[13px] py-2 border-b border-gray-100 last:border-0"
                                                    >
                                                        <div className="flex justify-between items-center gap-3">
                                                            <span className="text-gray-700 truncate">
                                                                {worker.worker_name}
                                                            </span>
                                                            <span className="text-[#3B0069] font-semibold whitespace-nowrap">
                                                                ₹{worker.balance.toLocaleString("en-IN")}
                                                            </span>
                                                        </div>

                                                        {boqItems.length > 0 && (
                                                            <div className="mt-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openMultiWorkerBoQModal(worker)}
                                                                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-dashed border-[#D6BBFB] bg-[#F9F5FF] px-3 py-2 text-left transition-colors hover:bg-[#F4EBFF]"
                                                                >
                                                                    {selectedWorkerBoQ ? (
                                                                        <span className="min-w-0">
                                                                            <span className="block truncate text-xs font-medium text-[#6941C6]">
                                                                                {selectedWorkerBoQ.task_name || selectedWorkerBoQ.item_name}
                                                                            </span>
                                                                            <span className="block truncate text-[11px] text-[#7F56D9]">
                                                                                {selectedWorkerBoQ.sectionName}
                                                                            </span>
                                                                        </span>
                                                                    ) : (
                                                                        <span className="flex min-w-0 items-center text-xs text-gray-500">
                                                                            <Link size={14} className="mr-2 shrink-0" />
                                                                            <span className="truncate">Link to Task</span>
                                                                        </span>
                                                                    )}
                                                                    <span className="shrink-0 text-[11px] font-semibold text-[#7F56D9]">
                                                                        {selectedWorkerBoQ ? "Change" : "Link"}
                                                                    </span>
                                                                </button>

                                                                {selectedWorkerBoQ && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeMultiWorkerBoQ(worker)}
                                                                        className="mt-1 text-[11px] font-medium text-red-500 hover:text-red-700"
                                                                    >
                                                                        Remove link
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <hr className="my-2 border-purple-300" />

                                        <div className="flex justify-between items-center bg-white rounded-md p-2">
                                            <span className="text-[#232323] text-[15px] font-semibold">
                                                Total Pending Amount
                                            </span>
                                            <span className="text-green-600 font-bold text-[16px]">
                                                ₹
                                                {defaulterWorkers
                                                    .reduce((sum, w) => sum + w.balance, 0)
                                                    .toLocaleString("en-IN")}
                                            </span>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                                            <p className="text-[12px] text-yellow-800">
                                                ⚠️ You must pay the exact total amount. No partial
                                                payments allowed.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}



                </div>

                {/* Right - Could add additional info or summary here */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[#232323] mb-2">
                            Select Payment Date
                        </label>
                        <div className="flex gap-3 items-center">
                            <button
                                onClick={() => {
                                    setDateMode("Now");
                                    const d = new Date();
                                    const dd = String(d.getDate()).padStart(2, "0");
                                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                                    const yyyy = d.getFullYear();
                                    setDateValue(`${yyyy}-${mm}-${dd}`);
                                }}
                                className="border-1 border-[#7F599D] h-[36px] bg-[#ECE6F0] text-[#3B0069] rounded-[12px] px-5 py-1 text-sm font-medium"
                            >
                                Now
                            </button>
                            <input
                                type="date"
                                value={dateValue}
                                onChange={(e) => {
                                    setDateValue(e.target.value);
                                    setDateMode("Custom");
                                }}
                                className="border rounded-lg px-4 py-2 text-[16px] text-[#232323] w-full bg-white"
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-[#232323] mb-2">
                                Paid Amount
                            </label>
                            <div className="relative w-full">
                                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
                                {/* <input
                type="number"
                min="0"
                placeholder="Amount"
                className="pl-8 pr-4 py-2 border rounded-lg text-sm w-full bg-white"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || parseFloat(val) >= 0) {
                    setAmount(val);
                  }
                }}
                disabled={isMultiPayment}
                title={
                  isMultiPayment
                    ? "Amount is fixed for multi-worker payment"
                    : ""
                }
                /> */}
                                <input
                                    type="number"
                                    min="0"
                                    max={99_900_000_000}
                                    placeholder="Amount"
                                    className="pl-8 pr-4 py-2 border rounded-lg text-sm w-full bg-white"
                                    value={amount}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "") {
                                            setAmount("");
                                            return;
                                        }
                                        const num = parseFloat(val);
                                        if (num < 0) return;
                                        if (num > 99_900_000_000) {
                                            setAmount("99900000000");
                                            return;
                                        }
                                        setAmount(val);
                                    }}
                                    disabled={isMultiPayment}
                                    title={
                                        isMultiPayment
                                            ? "Amount is fixed for multi-worker payment"
                                            : ""
                                    }
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                {formatAmountPreview(amount)}
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-[#232323] mb-2">
                                Payment Method
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {["Cash", "Online", "Cheque"].map((method) => (
                                    <button
                                        key={method}
                                        onClick={() => setPaymentMethod(method)}
                                        className={`px-4 py-1 rounded-full text-sm font-medium ${paymentMethod === method
                                            ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                                            : "bg-gray-200 text-gray-700"
                                            }`}
                                    >
                                        {method}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#232323] mb-2">
                            Remarks/ Comments (Optional)
                        </label>
                        <textarea
                            type="text"
                            placeholder="Type"
                            className="w-full border rounded-lg px-4 py-2 text-sm min-h-[60px] bg-white"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#232323] mb-2">
                            Payment Slip (Optional)
                        </label>
                        {!receiptPreviewUrl && (
                            <label className="w-full h-[36px] flex items-center justify-center px-4 py-8 bg-[#ECE6F0] rounded-lg text-sm text-[#3B0069] cursor-pointer hover:bg-gray-200">
                                <input
                                    type="file"
                                    ref={receiptInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                    accept="image/*"
                                    disabled={isSaveClicked}
                                />
                                <span className="flex items-center gap-2">
                                    <Upload size={16} />
                                    Upload Photos
                                </span>
                            </label>
                        )}
                        <div className="mt-3">
                            {receiptPreviewUrl && (
                                <div className="relative w-full h-48 rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
                                    <img
                                        src={receiptPreviewUrl}
                                        alt="Receipt"
                                        className="w-full h-full object-contain rounded-md"
                                    />
                                    {!isSaveClicked && (
                                        <button
                                            type="button"
                                            onClick={handleClearReceipt}
                                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            </div>
            {showBoQModal && typeof document !== "undefined"
                ? createPortal(renderBoQSelectionModal(), document.body)
                : null}
        </div>

        <div className="p-6 border-t border-gray-100 flex flex-col items-center justify-center shrink-0 bg-white">
            <button
                onClick={handleSave}
                disabled={
                    isLoading ||
                    isSaveClicked ||
                    (!selectedWorker && defaulterWorkers.length == 0) ||
                    !amount
                }
                className={`min-w-[240px] text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
                    isLoading ||
                    isSaveClicked ||
                    (!selectedWorker && defaulterWorkers.length == 0) ||
                    !amount
                        ? "bg-[#3B0069] cursor-not-allowed"
                        : "bg-[#3B0069] hover:bg-[#3B0069D9]"
                }`}
                style={{
                    opacity:
                        isLoading ||
                        isSaveClicked ||
                        (!selectedWorker && defaulterWorkers.length == 0) ||
                        !amount
                            ? 0.4
                            : 1,
                }}
            >
                {isLoading ? "Saving..." : "Save"}
                <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
                    {isLoading ? (
                        <Loader className="w-4 h-4 text-white animate-spin" />
                    ) : (
                        <Check size={18} className="text-white" />
                    )}
                </span>
            </button>
        </div>
    </div>
    );
};

export default WorkerPayment;
